import { type FC, useState, useContext } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import slugify from 'dibs-slugify/exports/slugify.ts';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { Badge } from 'dibs-elements/exports/Badge.tsx';
import { ExpandingAreaWithReadMore, ExpandingArea } from 'dibs-elements/exports/ExpandingArea.tsx';
import { RotatingArrow } from 'dibs-elements/exports/RotatingArrow.tsx';
import { usePdpDetails } from './detailHelpers.ts';
import { ServerVarsContext } from '../../utils/ServerVarsContext.tsx';
import { type NftPdpAbout_nftItem$key } from './__generated__/NftPdpAbout_nftItem.graphql.ts';
import styles from './NftPdpAbout.scss';

type Props = {
    nftItem: NftPdpAbout_nftItem$key;
};

function isIpfsUri(uri?: string | null): boolean {
    return !!uri?.startsWith('ipfs://');
}

function getIpfsCid(uri: string): string | null {
    if (uri.startsWith('ipfs://')) {
        return uri.split('ipfs://')[1];
    }
    return null;
}

const InteractiveDetailValue: FC<{ nftName: string; href: string; detailValue: string }> = ({
    nftName,
    href,
    detailValue,
}) => {
    const linkProps = /^data:/.test(href)
        ? {
              download: slugify(nftName),
          }
        : ({
              target: '_blank',
          } as const);
    return (
        <Link href={href} {...linkProps}>
            {detailValue}
        </Link>
    );
};

const BadgeLink: FC<{ href: string; detailValue: string }> = ({ href, detailValue }) => {
    return (
        <Link href={href} target="_blank">
            <Badge type="pill" size="medium-regular" interactive>
                {detailValue}
            </Badge>
        </Link>
    );
};

export const NftPdpAbout: FC<Props> = ({ nftItem: nftItemRef }) => {
    const { isMobile } = useContext(ServerVarsContext);
    const [isExpanded, setIsExpanded] = useState(false);

    const nftItem = useFragment(
        graphql`
            fragment NftPdpAbout_nftItem on NftItem {
                ...detailHelpers_nftItem
                tokenUri
                creatorAddress {
                    publicProfileUrl
                    publicProfile {
                        profileName
                    }
                }
                metadata {
                    ... on NftDigitalArtMetadata {
                        name
                        createdYear
                        description
                        media {
                            url
                        }
                    }
                }
                exhibitions {
                    urlLabel
                    heroBanner {
                        bannerConfig {
                            ... on NftCmsExhibitionHtmlBannerType {
                                eyeBrow
                            }
                        }
                    }
                }
            }
        `,
        nftItemRef
    );

    const [pdpDetails, pdpBadges] = usePdpDetails({ nftItem });
    const { metadata, creatorAddress } = nftItem || {};
    const { createdYear, name } = metadata || {};
    const displayName = creatorAddress?.publicProfile?.profileName;
    const profileUrl = creatorAddress?.publicProfileUrl || '';
    const nftExhibition = nftItem.exhibitions?.[0];

    const unavailable = (
        <FormattedMessage id="nft.pdp.about.unavailable" defaultMessage="Unavailable" />
    );

    return (
        <div className={styles.section}>
            <HeadingLevel>
                {Header => (
                    <div className={styles.container}>
                        {!isMobile && (
                            <div className={styles.desktopSection}>
                                <div className={styles.itemDetailText}>
                                    {displayName && <Link href={profileUrl}>{displayName}</Link>}
                                    {createdYear &&
                                        `${displayName && createdYear ? ', ' : ''}${createdYear}`}
                                </div>
                                {nftExhibition?.urlLabel && (
                                    <FormattedMessage
                                        id="nft.pdp.about.featuredIn"
                                        defaultMessage="Featured In {link}"
                                        values={{
                                            link: (
                                                <Link
                                                    href={`/nft-collections/exhibitions/${slugify(
                                                        nftExhibition.urlLabel
                                                    )}`}
                                                >
                                                    {nftExhibition?.heroBanner?.[0]?.bannerConfig
                                                        ?.eyeBrow || (
                                                        <FormattedMessage
                                                            id="nft.pdp.about.featuredInFallback"
                                                            defaultMessage="Exhibition"
                                                        />
                                                    )}
                                                </Link>
                                            ),
                                        }}
                                    />
                                )}
                            </div>
                        )}
                        <Header className={styles.descriptionHeader}>
                            {isMobile ? (
                                <FormattedMessage
                                    id="nft.pdp.about.header"
                                    defaultMessage="About"
                                />
                            ) : (
                                name
                            )}
                        </Header>
                        <ExpandingAreaWithReadMore
                            wrapperClass={styles.descriptionBody}
                            collapsedHeight={192}
                            showGradient={false}
                        >
                            <span
                                data-tn="nft-pdp-item-description-content"
                                dangerouslySetInnerHTML={{
                                    __html: metadata?.description || '',
                                }}
                            />
                        </ExpandingAreaWithReadMore>
                        <div className={styles.detailsAndCidWrapper}>
                            <dl className={styles.detailsContainer}>
                                {pdpDetails.filter(filterNulls).map(detail => (
                                    <div
                                        key={detail.detailKey}
                                        data-key={detail.detailKey}
                                        className={styles.detailItem}
                                    >
                                        {detail.detailDisplayName && (
                                            <dt className={styles.detailName}>
                                                {detail.detailDisplayName}
                                            </dt>
                                        )}
                                        {detail.href ? (
                                            <dd className={styles.detailValue}>
                                                <InteractiveDetailValue
                                                    nftName={metadata?.name ?? ''}
                                                    href={detail.href}
                                                    detailValue={detail.detailValue ?? ''}
                                                />
                                            </dd>
                                        ) : (
                                            <dd className={styles.detailValue}>
                                                {detail.detailValue}
                                            </dd>
                                        )}
                                    </div>
                                ))}
                            </dl>
                            {(isIpfsUri(metadata?.media?.url) || isIpfsUri(nftItem?.tokenUri)) && (
                                <div className={styles.cidDetails}>
                                    <Link
                                        dataTn={`cid-expanding-area`}
                                        className={styles.cidLink}
                                        onClick={() => setIsExpanded(v => !v)}
                                    >
                                        <span className={styles.cidName}>
                                            <FormattedMessage
                                                id="nft.pdp.about.cid"
                                                defaultMessage="CID"
                                            />
                                        </span>
                                        <RotatingArrow direction={isExpanded ? 'up' : 'down'} />
                                    </Link>

                                    <ExpandingArea expanded={isExpanded} showGradient={false}>
                                        <div className={styles.cidDetail}>
                                            <span className={styles.cidDetailName}>
                                                <FormattedMessage
                                                    id="nft.pdp.about.artWorkCid"
                                                    defaultMessage="Artwork CID"
                                                />
                                            </span>
                                            {': '}
                                            <span className={styles.cidDetailValue}>
                                                {isIpfsUri(metadata?.media?.url)
                                                    ? getIpfsCid(metadata?.media?.url || '')
                                                    : unavailable}
                                            </span>
                                        </div>
                                        <div className={styles.cidDetail}>
                                            <span className={styles.cidDetailName}>
                                                <FormattedMessage
                                                    id="nft.pdp.about.metadataCid"
                                                    defaultMessage="Token Metadata CID"
                                                />
                                            </span>
                                            {': '}
                                            <span className={styles.cidDetailValue}>
                                                {isIpfsUri(nftItem?.tokenUri)
                                                    ? getIpfsCid(nftItem.tokenUri || '')
                                                    : unavailable}
                                            </span>
                                        </div>
                                    </ExpandingArea>
                                </div>
                            )}
                            <div className={styles.badgeContainer}>
                                {pdpBadges.filter(filterNulls).map(badge => (
                                    <BadgeLink
                                        key={badge?.detailKey}
                                        href={badge.href ?? ''}
                                        detailValue={badge.detailDisplayName ?? ''}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </HeadingLevel>
        </div>
    );
};
